import React, { useContext, useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ProfileContext } from "@velaro/velaro-component-lib";
import { useLiveDashboard } from "@/hooks/useLiveDashboard";
import useApi from "@/hooks/useApi";

// ── Helpers ────────────────────────────────────────────────────────────────

function greeting(name: string): string {
  const h = new Date().getHours();
  return `${h < 12 ? "Good morning" : h < 17 ? "Good afternoon" : "Good evening"}, ${name}`;
}

function fmt(mins: number): string {
  if (mins < 1) return "< 1m";
  const m = Math.floor(mins);
  const s = Math.round((mins - m) * 60);
  return s > 0 ? `${m}m ${s}s` : `${m}m`;
}

function fmtSecs(secs: number): string {
  if (secs < 60) return `${Math.round(secs)}s`;
  const m = Math.floor(secs / 60);
  const s = Math.round(secs % 60);
  return s > 0 ? `${m}m ${s}s` : `${m}m`;
}

function todayRange() {
  const start = new Date(); start.setHours(0, 0, 0, 0);
  const end = new Date(); end.setHours(23, 59, 59, 999);
  return { startDate: start.toISOString(), endDate: end.toISOString() };
}

function thirtyDaysAgo() {
  const d = new Date(); d.setDate(d.getDate() - 30); d.setHours(0, 0, 0, 0);
  return d.toISOString();
}

// ── Live dot ───────────────────────────────────────────────────────────────

function LiveDot({ status }: { status: "connecting" | "live" | "reconnecting" | "error" }) {
  const color = status === "live" ? "bg-green-500" : status === "reconnecting" ? "bg-amber-400" : "bg-slate-400";
  return <span className={`inline-block w-2 h-2 rounded-full ${color} ${status === "live" ? "animate-pulse" : ""}`} />;
}

// ── Live KPI card ──────────────────────────────────────────────────────────

function LiveKpiCard({ label, value, iconClass, accent }: { label: string; value: string | number; iconClass: string; accent?: string }) {
  return (
    <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl p-4 flex items-start gap-3 shadow-sm">
      <div className={`rounded-lg p-2 shrink-0 ${accent ?? "bg-cornflower-blue-50 dark:bg-cornflower-blue-900/20"}`}>
        <i className={`${iconClass} text-sm`} aria-hidden="true" />
      </div>
      <div className="min-w-0">
        <div className="text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400 truncate">{label}</div>
        <div
          className={`font-bold text-slate-800 dark:text-slate-100 leading-tight ${String(value).length > 5 ? "text-lg" : "text-2xl"}`}
          title={String(value)}
        >
          {value}
        </div>
      </div>
    </div>
  );
}

// ── Shortcut types ─────────────────────────────────────────────────────────

interface Shortcut { id: string; label: string; to: string; iconClass: string; color: string }

const ALL_SHORTCUTS: Shortcut[] = [
  { id: "users",        label: "Users",           to: "/users",           iconClass: "fa-solid fa-users",           color: "bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400" },
  { id: "teams",        label: "Teams",           to: "/teams",           iconClass: "fa-solid fa-user-group",      color: "bg-indigo-100 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400" },
  { id: "workflows",    label: "Workflows",       to: "/workflows",       iconClass: "fa-solid fa-diagram-project", color: "bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400" },
  { id: "rules",        label: "Rules",           to: "/rules",           iconClass: "fa-solid fa-sliders",         color: "bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400" },
  { id: "integrations", label: "Integrations",    to: "/integrations",    iconClass: "fa-solid fa-plug",            color: "bg-orange-100 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400" },
  { id: "ai",           label: "AI Chatbot",      to: "/ai-new/settings", iconClass: "fa-solid fa-robot",           color: "bg-teal-100 dark:bg-teal-900/30 text-teal-600 dark:text-teal-400" },
  { id: "kb",           label: "Knowledge Base",  to: "/kb/articles",     iconClass: "fa-solid fa-book-open",       color: "bg-yellow-100 dark:bg-yellow-900/30 text-yellow-600 dark:text-yellow-400" },
  { id: "dashboard",    label: "Live Dashboard",  to: "/dashboard",       iconClass: "fa-solid fa-chart-line",      color: "bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400" },
  { id: "channels",     label: "Channels",        to: "/sms",             iconClass: "fa-solid fa-tower-broadcast", color: "bg-sky-100 dark:bg-sky-900/30 text-sky-600 dark:text-sky-400" },
  { id: "value-report",       label: "Value Report",       to: "/reports/value",              iconClass: "fa-solid fa-trophy",           color: "bg-violet-100 dark:bg-violet-900/30 text-violet-600 dark:text-violet-400" },
  { id: "agent-performance",  label: "Agent Performance",  to: "/reports/agent-performance",  iconClass: "fa-solid fa-user-graduate",    color: "bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400" },
  { id: "leaderboard",        label: "Leaderboard",        to: "/reports/leaderboard",        iconClass: "fa-solid fa-ranking-star",     color: "bg-yellow-100 dark:bg-yellow-900/30 text-yellow-600 dark:text-yellow-400" },
  { id: "chat-intelligence",  label: "Chat Intelligence",  to: "/reports/chat-intelligence",  iconClass: "fa-solid fa-brain",            color: "bg-indigo-100 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400" },
  { id: "csat-report",        label: "CSAT Results",       to: "/reports/csat",               iconClass: "fa-solid fa-star-half-stroke", color: "bg-pink-100 dark:bg-pink-900/30 text-pink-600 dark:text-pink-400" },
  { id: "billing",      label: "Billing",         to: "/billing",         iconClass: "fa-solid fa-credit-card",     color: "bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-400" },
  { id: "settings",     label: "Settings",        to: "/settings",        iconClass: "fa-solid fa-gear",            color: "bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-400" },
  { id: "surveys",      label: "Surveys",         to: "/surveys",         iconClass: "fa-solid fa-star",            color: "bg-pink-100 dark:bg-pink-900/30 text-pink-600 dark:text-pink-400" },
  { id: "email",        label: "Email Campaigns", to: "/email-campaigns", iconClass: "fa-solid fa-envelope",        color: "bg-rose-100 dark:bg-rose-900/30 text-rose-600 dark:text-rose-400" },
  { id: "deployment",   label: "Deployment",      to: "/deployment",      iconClass: "fa-solid fa-cloud-arrow-up",  color: "bg-cyan-100 dark:bg-cyan-900/30 text-cyan-600 dark:text-cyan-400" },
];

const DEFAULT_SHORTCUT_IDS = ["users", "teams", "workflows", "rules", "integrations", "ai", "kb", "dashboard"];
const SHORTCUTS_KEY = "admin_home_shortcuts";

function loadShortcutIds(): string[] {
  try { const r = localStorage.getItem(SHORTCUTS_KEY); if (r) return JSON.parse(r); } catch {}
  return DEFAULT_SHORTCUT_IDS;
}
function saveShortcutIds(ids: string[]) {
  try { localStorage.setItem(SHORTCUTS_KEY, JSON.stringify(ids)); } catch {}
}

// ── Shortcut card ──────────────────────────────────────────────────────────

function ShortcutCard({ shortcut, onRemove }: { shortcut: Shortcut; onRemove?: () => void }) {
  const navigate = useNavigate();
  const [hovered, setHovered] = useState(false);
  return (
    <div className="relative group" onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)}>
      <button
        onClick={() => navigate(shortcut.to)}
        className="w-full bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl p-4 flex flex-col items-center gap-2 text-center shadow-sm hover:shadow-md hover:border-cornflower-blue-300 dark:hover:border-cornflower-blue-600 transition-all hover:scale-[1.02]"
      >
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${shortcut.color}`}>
          <i className={`${shortcut.iconClass} text-base`} aria-hidden="true" />
        </div>
        <span className="text-xs font-semibold text-slate-700 dark:text-slate-300 leading-tight">{shortcut.label}</span>
      </button>
      {onRemove && hovered && (
        <button
          onClick={(e) => { e.stopPropagation(); onRemove(); }}
          className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-slate-500 hover:bg-red-500 text-white rounded-full flex items-center justify-center text-[10px] shadow transition-colors"
          aria-label={`Remove ${shortcut.label} shortcut`}
        >
          <i className="fa-solid fa-xmark" />
        </button>
      )}
    </div>
  );
}

// ── Shortcut picker ────────────────────────────────────────────────────────

function ShortcutPicker({ currentIds, onAdd, onClose }: { currentIds: string[]; onAdd: (id: string) => void; onClose: () => void }) {
  const available = ALL_SHORTCUTS.filter(s => !currentIds.includes(s.id));
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={onClose}>
      <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-xl p-5 sm:p-6 w-80 max-w-[calc(100vw-2rem)] max-h-[80vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-slate-800 dark:text-slate-100">Add Shortcut</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200" aria-label="Close">
            <i className="fa-solid fa-xmark text-sm" />
          </button>
        </div>
        {available.length === 0 ? (
          <p className="text-sm text-slate-500 text-center py-4">All shortcuts are already pinned.</p>
        ) : (
          <div className="flex flex-col gap-1">
            {available.map(s => (
              <button key={s.id} onClick={() => { onAdd(s.id); onClose(); }}
                className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 text-left transition-colors">
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${s.color}`}>
                  <i className={`${s.iconClass} text-sm`} aria-hidden="true" />
                </div>
                <span className="text-sm font-medium text-slate-700 dark:text-slate-300">{s.label}</span>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Team status ────────────────────────────────────────────────────────────

function TeamStatusRow({ team }: { team: any }) {
  return (
    <div className="flex items-center gap-3 py-2.5 border-b border-slate-100 dark:border-slate-700 last:border-0">
      <div className="flex-1 min-w-0">
        <div className="font-medium text-sm text-slate-800 dark:text-slate-100 truncate">{team.teamName}</div>
        <div className="text-xs text-slate-500 dark:text-slate-400">
          {team.openConversations} open · longest {fmt(team.longestWaitMinutes)}
        </div>
      </div>
      <div className="flex items-center gap-2 text-xs tabular-nums shrink-0">
        <span className="flex items-center gap-1 text-green-600 dark:text-green-400 font-semibold">
          <span className="w-1.5 h-1.5 bg-green-500 rounded-full" />
          {team.agentsAvailable}
        </span>
        <span className="text-slate-300 dark:text-slate-600">/</span>
        <span className="text-slate-500 dark:text-slate-400">{team.agentsOnline} online</span>
      </div>
    </div>
  );
}

// ── Stats widget system ────────────────────────────────────────────────────

type StatWidgetId = "csat" | "conversations-today" | "service-level" | "bot-analytics" | "campaign-health" | "agent-utilization";

interface StatWidgetDef {
  id: StatWidgetId;
  label: string;
  description: string;
  iconClass: string;
  color: string;
}

const STAT_WIDGET_DEFS: StatWidgetDef[] = [
  { id: "csat",                label: "Customer Satisfaction", description: "CSAT score and satisfaction rate (last 30 days)", iconClass: "fa-solid fa-star",       color: "text-yellow-500" },
  { id: "conversations-today", label: "Today's Conversations", description: "Serviced, missed, and abandoned counts for today",  iconClass: "fa-solid fa-comments",  color: "text-blue-500" },
  { id: "service-level",       label: "Service Level (SLA)",   description: "% of conversations answered within SLA threshold", iconClass: "fa-solid fa-gauge-high", color: "text-green-500" },
  { id: "bot-analytics",       label: "Bot Analytics",         description: "AI skill calls, success rate, top tool",           iconClass: "fa-solid fa-robot",     color: "text-purple-500" },
  { id: "campaign-health",     label: "Campaign Health",       description: "Email open rate, bounce rate, deliverability",     iconClass: "fa-solid fa-envelope",  color: "text-rose-500" },
  { id: "agent-utilization",   label: "Agent Utilization",     description: "Team-wide utilization and avg handle time",       iconClass: "fa-solid fa-user-clock", color: "text-indigo-500" },
];

const STAT_WIDGETS_KEY = "admin_home_stat_widgets";
const DEFAULT_STAT_WIDGETS: StatWidgetId[] = [];

function loadStatWidgets(): StatWidgetId[] {
  try {
    const r = localStorage.getItem(STAT_WIDGETS_KEY);
    if (r) {
      const parsed = JSON.parse(r) as string[];
      const validIds = new Set(STAT_WIDGET_DEFS.map(d => d.id));
      return parsed.filter(id => validIds.has(id as StatWidgetId)) as StatWidgetId[];
    }
  } catch {}
  return DEFAULT_STAT_WIDGETS;
}
function saveStatWidgets(ids: StatWidgetId[]) {
  try { localStorage.setItem(STAT_WIDGETS_KEY, JSON.stringify(ids)); } catch {}
}

// ── Individual stat widget data hooks ─────────────────────────────────────

function useCsatSummary() {
  const api = useApi();
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.admin.get("api/admin/Csat/Summary?days=30")
      .then(r => r.json()).then(setData).catch(() => {}).finally(() => setLoading(false));
  }, []);
  return { data, loading };
}

function useConversationsToday() {
  const api = useApi();
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    const { startDate, endDate } = todayRange();
    api.admin.get(`ConversationsReport?startDate=${encodeURIComponent(startDate)}&endDate=${encodeURIComponent(endDate)}&granularity=day`)
      .then(r => r.json()).then(setData).catch(() => {}).finally(() => setLoading(false));
  }, []);
  return { data, loading };
}

function useServiceLevel() {
  const api = useApi();
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    const start = thirtyDaysAgo();
    const end = new Date().toISOString();
    api.admin.get(`ServiceLevel?startDate=${encodeURIComponent(start)}&endDate=${encodeURIComponent(end)}&granularity=day`)
      .then(r => r.json()).then(setData).catch(() => {}).finally(() => setLoading(false));
  }, []);
  return { data, loading };
}

function useBotAnalytics() {
  const api = useApi();
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.admin.get("BotAnalytics/Summary?days=30")
      .then(r => r.json()).then(setData).catch(() => {}).finally(() => setLoading(false));
  }, []);
  return { data, loading };
}

function useCampaignHealth() {
  const api = useApi();
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.admin.get("CampaignReports/Deliverability?days=30")
      .then(r => r.json()).then(setData).catch(() => {}).finally(() => setLoading(false));
  }, []);
  return { data, loading };
}

function useAgentUtilization() {
  const api = useApi();
  const [data, setData] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    const end = new Date().toISOString();
    const start = thirtyDaysAgo();
    api.admin.get(`Reports/AgentUtilization?startDate=${encodeURIComponent(start)}&endDate=${encodeURIComponent(end)}`)
      .then(r => r.json()).then(d => setData(Array.isArray(d) ? d : [])).catch(() => {}).finally(() => setLoading(false));
  }, []);
  return { data, loading };
}

// ── Stat widget card shell ─────────────────────────────────────────────────

function StatCard({ title, iconClass, iconColor, loading, onRemove, children }: {
  title: string; iconClass: string; iconColor: string;
  loading: boolean; onRemove: () => void; children: React.ReactNode;
}) {
  return (
    <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl p-4 shadow-sm relative group">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <i className={`${iconClass} ${iconColor} text-sm`} aria-hidden="true" />
          <span className="text-xs font-semibold text-slate-600 dark:text-slate-400 uppercase tracking-wide">{title}</span>
        </div>
        <button
          onClick={onRemove}
          className="opacity-0 group-hover:opacity-100 text-slate-300 hover:text-red-400 transition-opacity text-xs"
          aria-label={`Remove ${title} widget`}
          title="Remove widget"
        >
          <i className="fa-solid fa-xmark" />
        </button>
      </div>
      {loading ? (
        <div className="space-y-2">
          <div className="h-7 rounded bg-slate-100 dark:bg-slate-700 animate-pulse" />
          <div className="h-4 w-3/4 rounded bg-slate-100 dark:bg-slate-700 animate-pulse" />
        </div>
      ) : children}
    </div>
  );
}

// ── Individual stat widget renderers ───────────────────────────────────────

function CsatWidget({ onRemove }: { onRemove: () => void }) {
  const { data, loading } = useCsatSummary();
  const stars = data?.averageRating ? Math.round(data.averageRating) : 0;
  return (
    <StatCard title="Customer Satisfaction" iconClass="fa-solid fa-star" iconColor="text-yellow-500" loading={loading} onRemove={onRemove}>
      {data ? (
        <div>
          <div className="flex items-baseline gap-2 mb-1">
            <span className="text-3xl font-bold text-slate-800 dark:text-slate-100">
              {data.averageRating?.toFixed(1) ?? "—"}
            </span>
            <span className="text-slate-400 text-sm">/ 5</span>
          </div>
          <div className="flex gap-0.5 mb-2">
            {[1,2,3,4,5].map(n => (
              <i key={n} className={`fa-solid fa-star text-xs ${n <= stars ? "text-yellow-400" : "text-slate-200 dark:text-slate-600"}`} />
            ))}
          </div>
          <div className="flex items-center justify-between text-sm">
            <span className="text-slate-500 dark:text-slate-400">{data.totalResponses ?? 0} responses</span>
            <span className={`font-semibold ${(data.satisfactionRate ?? 0) >= 80 ? "text-violet-600 dark:text-violet-400" : (data.satisfactionRate ?? 0) >= 60 ? "text-amber-600 dark:text-amber-400" : "text-rose-600 dark:text-rose-400"}`}>
              {data.satisfactionRate?.toFixed(0) ?? 0}% satisfied
            </span>
          </div>
          <Link to="/surveys" className="text-xs text-cornflower-blue-500 hover:underline mt-2 block">View responses</Link>
        </div>
      ) : (
        <p className="text-sm text-slate-400 dark:text-slate-500">No CSAT data available.</p>
      )}
    </StatCard>
  );
}

function ConversationsTodayWidget({ onRemove }: { onRemove: () => void }) {
  const { data, loading } = useConversationsToday();
  const s = data?.summary;
  return (
    <StatCard title="Today's Conversations" iconClass="fa-solid fa-comments" iconColor="text-blue-500" loading={loading} onRemove={onRemove}>
      {s ? (
        <div>
          <div className="flex items-baseline gap-2 mb-3">
            <span className="text-3xl font-bold text-slate-800 dark:text-slate-100">{s.total ?? 0}</span>
            <span className="text-slate-400 text-sm">total</span>
          </div>
          <div className="grid grid-cols-3 gap-2 text-center">
            <div>
              <div className="text-lg font-bold text-green-600 dark:text-green-400">{s.serviced ?? 0}</div>
              <div className="text-xs text-slate-400">Serviced</div>
            </div>
            <div>
              <div className="text-lg font-bold text-amber-500">{s.missed ?? 0}</div>
              <div className="text-xs text-slate-400">Missed</div>
            </div>
            <div>
              <div className="text-lg font-bold text-red-500">{s.abandoned ?? 0}</div>
              <div className="text-xs text-slate-400">Abandoned</div>
            </div>
          </div>
          {s.avgHandleTimeSeconds > 0 && (
            <div className="mt-2 text-xs text-slate-400">Avg handle: {fmtSecs(s.avgHandleTimeSeconds)}</div>
          )}
        </div>
      ) : (
        <p className="text-sm text-slate-400 dark:text-slate-500">No conversation data yet today.</p>
      )}
    </StatCard>
  );
}

function ServiceLevelWidget({ onRemove }: { onRemove: () => void }) {
  const { data, loading } = useServiceLevel();
  const s = data?.summary;
  const pct = s?.serviceLevelPct ?? 0;
  const color = pct >= 90 ? "text-green-600 dark:text-green-400" : pct >= 70 ? "text-amber-600 dark:text-amber-400" : "text-red-500";
  const barColor = pct >= 90 ? "bg-green-500" : pct >= 70 ? "bg-amber-400" : "bg-red-500";
  return (
    <StatCard title="Service Level (SLA)" iconClass="fa-solid fa-gauge-high" iconColor="text-green-500" loading={loading} onRemove={onRemove}>
      {s ? (
        <div>
          <div className={`text-3xl font-bold mb-1 ${color}`}>{pct.toFixed(1)}%</div>
          <div className="w-full h-1.5 bg-slate-100 dark:bg-slate-700 rounded-full mb-3">
            <div className={`h-full rounded-full ${barColor} transition-all`} style={{ width: `${Math.min(pct, 100)}%` }} />
          </div>
          <div className="grid grid-cols-2 gap-2 text-xs text-slate-500 dark:text-slate-400">
            <div><span className="font-medium text-slate-700 dark:text-slate-300">{s.totalServiced}</span> serviced</div>
            <div><span className="font-medium text-slate-700 dark:text-slate-300">{s.totalMissed}</span> missed</div>
            <div>Avg wait: {fmtSecs(s.avgQueueWaitSeconds ?? 0)}</div>
            <div>Threshold: {fmtSecs(s.slaThresholdSeconds ?? 0)}</div>
          </div>
        </div>
      ) : (
        <p className="text-sm text-slate-400 dark:text-slate-500">No SLA data available.</p>
      )}
    </StatCard>
  );
}

function BotAnalyticsWidget({ onRemove }: { onRemove: () => void }) {
  const { data, loading } = useBotAnalytics();
  return (
    <StatCard title="Bot Analytics" iconClass="fa-solid fa-robot" iconColor="text-purple-500" loading={loading} onRemove={onRemove}>
      {data ? (
        <div>
          <div className="flex items-baseline gap-2 mb-3">
            <span className="text-3xl font-bold text-slate-800 dark:text-slate-100">{data.totalSkillCalls ?? 0}</span>
            <span className="text-slate-400 text-sm">AI skill calls</span>
          </div>
          <div className="grid grid-cols-2 gap-2 text-xs text-slate-500 dark:text-slate-400 mb-2">
            <div>
              <span className={`font-semibold ${(data.successRate ?? 0) >= 90 ? "text-green-600 dark:text-green-400" : "text-amber-600"}`}>
                {data.successRate?.toFixed(1) ?? 0}%
              </span> success
            </div>
            <div><span className="font-medium text-red-500">{data.errorCount ?? 0}</span> errors</div>
          </div>
          {data.topSkill && (
            <div className="text-xs text-slate-400">
              Top: <span className="font-medium text-slate-600 dark:text-slate-300">{data.topSkill}</span>
              {data.topSkillCount ? ` (${data.topSkillCount}x)` : ""}
            </div>
          )}
          <Link to="/ai-new/settings" className="text-xs text-cornflower-blue-500 hover:underline mt-2 block">View chatbot settings</Link>
        </div>
      ) : (
        <p className="text-sm text-slate-400 dark:text-slate-500">No bot data available.</p>
      )}
    </StatCard>
  );
}

function CampaignHealthWidget({ onRemove }: { onRemove: () => void }) {
  const { data, loading } = useCampaignHealth();

  function statusDot(s: string) {
    return s === "green" ? "text-green-500" : s === "yellow" ? "text-amber-400" : "text-red-500";
  }

  return (
    <StatCard title="Campaign Health" iconClass="fa-solid fa-envelope" iconColor="text-rose-500" loading={loading} onRemove={onRemove}>
      {data ? (
        <div>
          <div className="space-y-2">
            <div className="flex items-center justify-between text-sm">
              <span className="text-slate-500 dark:text-slate-400">Open rate</span>
              <span className={`font-semibold ${statusDot(data.openStatus)}`}>
                {data.openRate?.toFixed(1) ?? 0}%
              </span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-slate-500 dark:text-slate-400">Bounce rate</span>
              <span className={`font-semibold ${statusDot(data.bounceStatus)}`}>
                {data.bounceRate?.toFixed(2) ?? 0}%
              </span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-slate-500 dark:text-slate-400">Unsub rate</span>
              <span className={`font-semibold ${statusDot(data.unsubStatus)}`}>
                {data.unsubRate?.toFixed(2) ?? 0}%
              </span>
            </div>
          </div>
          <div className="text-xs text-slate-400 mt-2">{data.totalSent?.toLocaleString() ?? 0} emails sent</div>
          <Link to="/email-campaigns" className="text-xs text-cornflower-blue-500 hover:underline mt-1 block">View campaigns</Link>
        </div>
      ) : (
        <p className="text-sm text-slate-400 dark:text-slate-500">No campaign data available.</p>
      )}
    </StatCard>
  );
}

const UTIL_COLLAPSED = 5;

function AgentUtilizationWidget({ onRemove }: { onRemove: () => void }) {
  const { data, loading } = useAgentUtilization();
  const { data: liveData } = useLiveDashboard();
  const [selectedTeamId, setSelectedTeamId] = useState<number | "all">("all");
  const [agentsExpanded, setAgentsExpanded] = useState(false);

  const agents = Array.isArray(data) ? data : [];
  const teams = liveData?.teams ?? [];

  // Build set of userIds for the selected team using live agent membership
  const teamAgentIds: Set<number> | null = selectedTeamId === "all"
    ? null
    : new Set(teams.find(t => t.teamId === selectedTeamId)?.agents.map(a => a.userId) ?? []);

  const filtered = teamAgentIds === null
    ? agents
    : agents.filter(a => teamAgentIds.has(a.userId));

  const sorted = [...filtered].sort((a, b) => (b.utilization ?? 0) - (a.utilization ?? 0));
  const displayed = agentsExpanded ? sorted : sorted.slice(0, UTIL_COLLAPSED);

  const avgUtil = agents.length > 0
    ? (agents.reduce((sum, a) => sum + (a.utilization ?? 0), 0) / agents.length).toFixed(1)
    : null;
  const avgHandle = agents.length > 0
    ? agents.reduce((sum, a) => sum + (a.avgEngagementSeconds ?? 0), 0) / agents.length
    : null;

  return (
    <StatCard title="Agent Utilization" iconClass="fa-solid fa-user-clock" iconColor="text-indigo-500" loading={loading} onRemove={onRemove}>
      {agents.length > 0 ? (
        <div>
          <div className="flex items-baseline gap-2 mb-2">
            <span className="text-3xl font-bold text-slate-800 dark:text-slate-100">{avgUtil}%</span>
            <span className="text-slate-400 text-sm">avg utilization</span>
          </div>
          <div className="grid grid-cols-2 gap-2 text-xs text-slate-500 dark:text-slate-400 mb-3">
            <div>{agents.length} agents</div>
            {avgHandle !== null && <div>Avg handle: {fmtSecs(avgHandle)}</div>}
          </div>

          {/* Team filter — only shown when multiple teams exist */}
          {teams.length > 1 && (
            <select
              value={selectedTeamId}
              onChange={e => {
                setSelectedTeamId(e.target.value === "all" ? "all" : Number(e.target.value));
                setAgentsExpanded(false);
              }}
              className="w-full text-xs border border-slate-200 dark:border-slate-600 rounded-lg px-2 py-1 mb-3 bg-white dark:bg-slate-700 text-slate-700 dark:text-slate-300 focus:outline-none focus:ring-1 focus:ring-cornflower-blue-400"
            >
              <option value="all">All Teams</option>
              {teams.map(t => (
                <option key={t.teamId} value={t.teamId}>{t.teamName}</option>
              ))}
            </select>
          )}

          {/* Agent bars with names */}
          <div className="space-y-1.5 pr-2">
            {displayed.length === 0 ? (
              <p className="text-xs text-slate-400 dark:text-slate-500 py-1">No agents in this team.</p>
            ) : displayed.map((a, i) => (
              <div key={i} className="flex items-center gap-2 text-xs">
                <span className="truncate text-slate-600 dark:text-slate-400 w-20 shrink-0">
                  {a.agent ?? a.agentName ?? a.displayName ?? `Agent ${i + 1}`}
                </span>
                <div className="flex-1 h-1.5 bg-slate-100 dark:bg-slate-700 rounded-full">
                  <div className="h-full bg-indigo-400 rounded-full" style={{ width: `${Math.min(a.utilization ?? 0, 100)}%` }} />
                </div>
                <span className="shrink-0 text-slate-500 w-8 text-right">{(a.utilization ?? 0).toFixed(0)}%</span>
              </div>
            ))}
          </div>

          {sorted.length > UTIL_COLLAPSED && (
            <button
              onClick={() => setAgentsExpanded(v => !v)}
              className="text-xs text-cornflower-blue-500 hover:underline mt-2 block"
            >
              {agentsExpanded ? "Show less" : `View all ${sorted.length} agents`}
            </button>
          )}
        </div>
      ) : (
        <p className="text-sm text-slate-400 dark:text-slate-500">No utilization data available.</p>
      )}
    </StatCard>
  );
}

const STAT_WIDGET_COMPONENTS: Record<StatWidgetId, React.ComponentType<{ onRemove: () => void }>> = {
  "csat":                CsatWidget,
  "conversations-today": ConversationsTodayWidget,
  "service-level":       ServiceLevelWidget,
  "bot-analytics":       BotAnalyticsWidget,
  "campaign-health":     CampaignHealthWidget,
  "agent-utilization":   AgentUtilizationWidget,
};

// ── Widget picker modal ────────────────────────────────────────────────────

function WidgetPicker({ currentIds, onAdd, onClear, onClose }: {
  currentIds: StatWidgetId[];
  onAdd: (id: StatWidgetId) => void;
  onClear: () => void;
  onClose: () => void;
}) {
  const available = STAT_WIDGET_DEFS.filter(w => !currentIds.includes(w.id));
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={onClose}>
      <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-xl p-5 sm:p-6 w-96 max-w-[calc(100vw-2rem)] max-h-[80vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="font-semibold text-slate-800 dark:text-slate-100">Add Dashboard Widget</h3>
            <p className="text-xs text-slate-400 mt-0.5">Choose metrics to display on your dashboard</p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200" aria-label="Close">
            <i className="fa-solid fa-xmark text-sm" />
          </button>
        </div>
        {available.length === 0 ? (
          <div className="text-center py-4">
            <p className="text-sm text-slate-500 mb-3">All widgets are already on your dashboard.</p>
            <button
              onClick={() => { onClear(); onClose(); }}
              className="text-xs text-slate-400 hover:text-red-500 underline transition-colors"
            >
              Remove all widgets
            </button>
          </div>
        ) : (
          <div className="flex flex-col gap-2">
            {available.map(w => (
              <button key={w.id} onClick={() => { onAdd(w.id); onClose(); }}
                className="flex items-start gap-3 px-4 py-3 rounded-xl border border-slate-200 dark:border-slate-700 hover:border-cornflower-blue-300 dark:hover:border-cornflower-blue-600 hover:bg-slate-50 dark:hover:bg-slate-700/50 text-left transition-all">
                <i className={`${w.iconClass} ${w.color} text-lg mt-0.5`} aria-hidden="true" />
                <div>
                  <div className="text-sm font-semibold text-slate-700 dark:text-slate-300">{w.label}</div>
                  <div className="text-xs text-slate-400 mt-0.5">{w.description}</div>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Main Home ──────────────────────────────────────────────────────────────

export default function Home() {
  const { profile } = useContext(ProfileContext);
  const { data, streamStatus } = useLiveDashboard();

  const [shortcutIds, setShortcutIds] = useState<string[]>(loadShortcutIds);
  const [showShortcutPicker, setShowShortcutPicker] = useState(false);
  const [shortcutEditMode, setShortcutEditMode] = useState(false);

  const [statWidgets, setStatWidgets] = useState<StatWidgetId[]>(loadStatWidgets);
  const [showWidgetPicker, setShowWidgetPicker] = useState(false);
  const [statsExpanded, setStatsExpanded] = useState(false);
  const STATS_COLLAPSED_COUNT = 3;

  const firstName = profile?.firstName || profile?.displayName?.split(" ")[0] || "there";

  const addShortcut = (id: string) => {
    const next = [...shortcutIds, id];
    setShortcutIds(next); saveShortcutIds(next);
  };
  const removeShortcut = (id: string) => {
    const next = shortcutIds.filter(s => s !== id);
    setShortcutIds(next); saveShortcutIds(next);
  };
  const addStatWidget = (id: StatWidgetId) => {
    const next = [...statWidgets, id];
    setStatWidgets(next); saveStatWidgets(next);
  };
  const removeStatWidget = useCallback((id: StatWidgetId) => {
    setStatWidgets(prev => { const next = prev.filter(w => w !== id); saveStatWidgets(next); return next; });
  }, []);

  const activeShortcuts = shortcutIds.map(id => ALL_SHORTCUTS.find(s => s.id === id)).filter(Boolean) as Shortcut[];
  const longestWait = data?.kpis?.longestWaitMinutes ?? 0;

  return (
    <div className="max-w-6xl mx-auto space-y-6">

      {/* Welcome header */}
      <div className="bg-gradient-to-br from-cornflower-blue-600 to-cornflower-blue-700 dark:from-slate-800 dark:to-slate-900 rounded-2xl px-5 sm:px-6 py-5 text-white shadow-sm flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-4">
        <div>
          <h1 className="text-xl font-bold leading-tight">{greeting(firstName)}</h1>
          <p className="text-cornflower-blue-100 dark:text-slate-400 text-sm mt-0.5">
            {new Date().toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" })}
          </p>
        </div>
        <div className="flex items-center gap-2 bg-white/10 dark:bg-white/5 rounded-lg px-3 py-2 text-sm">
          <LiveDot status={streamStatus} />
          <span className="text-white/80">
            {streamStatus === "live" ? "Live" : streamStatus === "reconnecting" ? "Reconnecting..." : "Connecting..."}
          </span>
        </div>
      </div>

      {/* Live KPIs */}
      <div>
        <h2 className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide mb-3">Live Operations</h2>
        {data ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2 sm:gap-3">
            <LiveKpiCard label="Online"       value={data.kpis.agentsOnline}      iconClass="fa-solid fa-circle-dot text-cornflower-blue-500 dark:text-cornflower-blue-400" />
            <LiveKpiCard label="Available"    value={data.kpis.agentsAvailable}   iconClass="fa-solid fa-circle-check text-green-500" accent="bg-green-50 dark:bg-green-900/20" />
            <LiveKpiCard label="Busy"         value={data.kpis.agentsBusy}        iconClass="fa-solid fa-circle-pause text-red-500"   accent="bg-red-50 dark:bg-red-900/20" />
            <LiveKpiCard label="Open Chats"   value={data.kpis.openConversations} iconClass="fa-solid fa-comments text-purple-500"    accent="bg-purple-50 dark:bg-purple-900/20" />
            <LiveKpiCard label="Longest Wait" value={fmt(longestWait)}            iconClass={`fa-solid fa-clock ${longestWait > 10 ? "text-amber-500" : "text-cornflower-blue-400"}`} accent={longestWait > 10 ? "bg-amber-50 dark:bg-amber-900/20" : undefined} />
            <LiveKpiCard label="Avg Wait"     value={fmt(data.kpis.avgWaitMinutes)} iconClass="fa-solid fa-gauge text-cornflower-blue-400 dark:text-cornflower-blue-500" />
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2 sm:gap-3">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl p-4 flex items-start gap-3 animate-pulse">
                <div className="w-9 h-9 rounded-lg bg-slate-200 dark:bg-slate-700 shrink-0" />
                <div className="flex-1 space-y-2 pt-1">
                  <div className="h-2.5 w-12 bg-slate-200 dark:bg-slate-700 rounded" />
                  <div className="h-5 w-8 bg-slate-200 dark:bg-slate-700 rounded" />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Shortcuts + Team status */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

        {/* Shortcuts */}
        <div className="lg:col-span-2">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide">Quick Access</h2>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setShortcutEditMode(v => !v)}
                className={`text-xs px-2 py-1 rounded-lg transition-colors ${
                  shortcutEditMode
                    ? "bg-cornflower-blue-100 text-cornflower-blue-700 dark:bg-cornflower-blue-900/30 dark:text-cornflower-blue-300"
                    : "text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700"
                }`}
              >
                {shortcutEditMode ? "Done" : "Edit"}
              </button>
              <button
                onClick={() => setShowShortcutPicker(true)}
                className="text-xs px-2 py-1 rounded-lg text-cornflower-blue-600 dark:text-cornflower-blue-400 hover:bg-cornflower-blue-50 dark:hover:bg-cornflower-blue-900/20 transition-colors"
              >
                + Add
              </button>
            </div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 xl:grid-cols-6 gap-3">
            {activeShortcuts.map(s => (
              <ShortcutCard
                key={s.id}
                shortcut={s}
                onRemove={shortcutEditMode ? () => removeShortcut(s.id) : undefined}
              />
            ))}
            {activeShortcuts.length === 0 && (
              <div className="col-span-full text-sm text-slate-400 dark:text-slate-500 text-center py-8 bg-white dark:bg-slate-800 rounded-xl border border-dashed border-slate-200 dark:border-slate-700">
                No shortcuts yet.{" "}
                <button onClick={() => setShowShortcutPicker(true)} className="text-cornflower-blue-500 hover:underline">
                  Add one
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Team status */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide">Team Status</h2>
            <Link to="/dashboard" className="text-xs text-cornflower-blue-500 dark:text-cornflower-blue-400 hover:underline">
              Full view
            </Link>
          </div>
          <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl px-4 py-2 shadow-sm min-h-[140px]">
            {data ? (
              data.teams.length === 0 ? (
                <div className="flex items-center justify-center h-24 text-sm text-slate-400 dark:text-slate-500">No teams configured.</div>
              ) : (
                <div className="max-h-64 overflow-y-auto pr-2">
                  {data.teams.map(team => <TeamStatusRow key={team.teamId} team={team} />)}
                </div>
              )
            ) : (
              <div className="space-y-3 py-2">
                {Array.from({ length: 3 }).map((_, i) => (
                  <div key={i} className="h-10 rounded-lg bg-slate-100 dark:bg-slate-700 animate-pulse" />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Customizable stats widgets */}
      {(statWidgets.length > 0 || true) && (
        <div>
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide">My Stats</h2>
            <div className="flex items-center gap-2">
              {statWidgets.length > STATS_COLLAPSED_COUNT && (
                <button
                  onClick={() => setStatsExpanded(v => !v)}
                  className="text-xs px-2 py-1 rounded-lg text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
                >
                  {statsExpanded ? "Show less" : `View all (${statWidgets.length})`}
                </button>
              )}
              <button
                onClick={() => setShowWidgetPicker(true)}
                className="text-xs px-2 py-1 rounded-lg text-cornflower-blue-600 dark:text-cornflower-blue-400 hover:bg-cornflower-blue-50 dark:hover:bg-cornflower-blue-900/20 transition-colors"
              >
                + Add widget
              </button>
            </div>
          </div>
          {statWidgets.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
              {(statsExpanded ? statWidgets : statWidgets.slice(0, STATS_COLLAPSED_COUNT)).map(id => {
                const Widget = STAT_WIDGET_COMPONENTS[id];
                return Widget ? <Widget key={id} onRemove={() => removeStatWidget(id)} /> : null;
              })}
            </div>
          ) : (
            <div className="text-center py-10 bg-white dark:bg-slate-800 border border-dashed border-slate-200 dark:border-slate-700 rounded-xl">
              <i className="fa-solid fa-chart-bar text-3xl text-slate-200 dark:text-slate-600 mb-3" aria-hidden="true" />
              <p className="text-sm text-slate-400 dark:text-slate-500 mb-3">No stats widgets yet. Add metrics that matter to you.</p>
              <button
                onClick={() => setShowWidgetPicker(true)}
                className="text-sm px-4 py-2 bg-cornflower-blue-500 hover:bg-cornflower-blue-600 text-white rounded-lg transition-colors"
              >
                Add your first widget
              </button>
              <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-1 mt-4 text-xs text-slate-400 px-4">
                {STAT_WIDGET_DEFS.slice(0, 3).map(w => (
                  <span key={w.id} className="flex items-center gap-1 whitespace-nowrap">
                    <i className={`${w.iconClass} ${w.color}`} aria-hidden="true" />
                    {w.label}
                  </span>
                ))}
                <span className="whitespace-nowrap">+ {STAT_WIDGET_DEFS.length - 3} more</span>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Modals */}
      {showShortcutPicker && (
        <ShortcutPicker currentIds={shortcutIds} onAdd={addShortcut} onClose={() => setShowShortcutPicker(false)} />
      )}
      {showWidgetPicker && (
        <WidgetPicker
          currentIds={statWidgets}
          onAdd={addStatWidget}
          onClear={() => { setStatWidgets([]); saveStatWidgets([]); }}
          onClose={() => setShowWidgetPicker(false)}
        />
      )}
    </div>
  );
}
